import React, { SFC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TagModel, getTagNameByType } from '../../../shared/types/tag'
import { Grid, CircularProgress, Card, CardContent, Typography, ListItem, TextField, InputAdornment } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'
import { useCalcHeightToBottomPartialList } from '../../../utils/ui'
import { NoData } from '../../../shared/components/NoData'
import SearchIcon from '@material-ui/icons/Search'

const useStyles = makeStyles((theme) => ({
    list: {
        overflowY: 'auto',
        marginTop: theme.spacing(3),
    },
    listItem: {
        margin: theme.spacing(1, 1, 2, 1),
        display: 'flex',
        '& .Mui-selected': {
            borderBottom: `3px solid ${theme.palette.primary.main}`,
            paddingBottom: theme.spacing(2) - 3,
        },
    },
    listItemButton: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'start',
        padding: theme.spacing(2),
    },
    listContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    listContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        '&:last-child': {
            padding: 0,
        },
    },
    search: {
        paddingLeft: theme.spacing(1),
    },
    progress: {
        textAlign: 'center',
        position: 'relative',
        top: '5px'
    },
    dataInfoWrapper: {
        textAlign: 'center',
        paddingLeft: theme.spacing(1),
    },
    input: {
        backgroundColor: '#f5f5f5', 
        borderRadius: theme.shape.borderRadius, 
        overflow: 'hidden',
        border: '1px solid #ddd',

        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5', 
            borderRadius: theme.shape.borderRadius, 
            paddingBottom: '6px',
            height: '40px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none', 
            },
        },
        '& .MuiFilledInput-input': {
            padding: '17px 12px 10px 0px'
        },
        '& .MuiInputLabel-root': {
            top: '-7px', 
            fontSize: '0.9rem', 
            // color: '#9e9e9e',
        },
        '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
            marginTop: '8px',
        },
    }
}))

interface TagsListProps {
    tags: TagModel[]
    selectedTag?: TagModel
    onTagSelected(tag: TagModel): void
}
const TagList: SFC<TagsListProps> = ({ tags, selectedTag, onTagSelected }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const tagsExecuted = useSelector((state: any) => state.tags.items.all.executed)

    const [search, setSearch] = useState('')

    const filteredTags = tags.filter((tag) => tag.name.toLowerCase().includes(search.toLowerCase()))

    const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value)
    }

    const listHeightFill = useCalcHeightToBottomPartialList('listWrapper')

    return (
        <>
            <Grid container>
                <Grid item xs={10} className={classes.search}>
                    <TextField
                        fullWidth
                        variant="filled"
                        placeholder={t('tags.search')}
                        value={search}
                        onChange={handleSearchChanged}
                        className={classes.input}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />{' '}
                </Grid>
                <Grid item xs={2} className={classes.progress}>
                    {!tagsExecuted && <CircularProgress size={30} />}
                </Grid>
                <Grid id="listWrapper" item xs={12} className={classes.list} ref={listHeightFill}>
                    {tagsExecuted && filteredTags.length === 0 ? (
                        <div className={classes.dataInfoWrapper}>
                            <NoData text={t('tags.noTags')} />
                        </div>
                    ) : (
                        filteredTags.map((tag) => (
                            <Card key={tag.id} className={classes.listItem} elevation={1}>
                                <ListItem
                                    button
                                    selected={tag === selectedTag}
                                    onClick={() => onTagSelected(tag)}
                                    className={classes.listItemButton}
                                >
                                    <div className={classes.listContentWrapper}>
                                        <CardContent className={classes.listContent}>
                                            <Typography variant="h6">{tag.name}</Typography>
                                            <Typography component="div" variant="caption">
                                                {getTagNameByType(tag.type)}
                                            </Typography>
                                        </CardContent>
                                    </div>
                                </ListItem>
                            </Card>
                        ))
                    )}
                </Grid>
            </Grid>
        </>
    )
}

export default TagList
